import ImageIcon from '@mui/icons-material/Image';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormLabel,
    Stack,
    Typography,
} from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { Controller, FieldValues } from 'react-hook-form';

import { useGetInstanceInfo } from '../../Hooks/useGetInstanceInfo';
import { customErrorToast, customPromiseToast, imageModerationError, imageUploadPromise } from '../../Utils/toasts';
import { FetcherError, useSSOFetchCloud } from '../Backend/SSOCloudFetcher';
import { BaseInputProps } from './GetFormInput';

export function FormImage<T extends FieldValues>({
    buttonText = 'Preview',
    control,
    helperText = 'Click to upload an image',
    imageHeight,
    imageWidth,
    label,
    maxHeight = 1080,
    maxWidth = 1920,
    required,
    value,
    withoutEnlargement = true,
}: Readonly<BaseInputProps<T>>) {
    const { fetchCloud } = useSSOFetchCloud();
    const { instanceInfo } = useGetInstanceInfo();

    const [file, setFile] = useState<File | null>(null);
    const [image, setImage] = useState<string | null>(null);
    const [imageUploaded, setImageUploaded] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [imageDimensions, setImageDimensions] = useState<{
        aspectRatio: string;
        height: number;
        width: number;
    } | null>(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            setErrorMessage(null);
            const reader = new FileReader();

            reader.onload = (event) => {
                const dataUrl = event.target?.result as string;
                setImage(dataUrl);

                // Check image dimensions
                const img = new Image();
                img.onload = () => {
                    const dimensions = {
                        aspectRatio: (img.width / img.height).toFixed(2),
                        height: img.height,
                        width: img.width,
                    };

                    setImageDimensions(dimensions);
                    console.log('Image dimensions:', dimensions);

                    // Validate image dimensions
                    if (img.width > maxWidth || img.height > maxHeight) {
                        setErrorMessage(
                            `Image dimensions (${String(img.width)}x${String(img.height)}) exceed maximum allowed size (${String(maxWidth)}x${String(maxHeight)})`,
                        );
                    }
                };
                img.src = dataUrl;
            };
            reader.readAsDataURL(selectedFile);
        }
        setImageUploaded(false);
    };

    const formValue = value;

    const handleImageUpload = async (onChange: (value: string) => void) => {
        if (file && !errorMessage) {
            const width = imageWidth || imageDimensions?.width || 800;
            const height = imageHeight || imageDimensions?.height || 300;

            try {
                const res = await customPromiseToast(
                    fetchCloud<{ fileID: string }>(
                        `upload/image?width=${String(width)}&height=${String(height)}&role=${instanceInfo.instance_role}&loc=${window.location.pathname}&withoutEnlargement=${String(withoutEnlargement)}`,
                        'post',
                        {
                            file: file,
                        },
                        true,
                        true,
                    ),
                    imageUploadPromise,
                );
                onChange(`i/${(res as { fileID: string }).fileID}`);
                setImageUploaded(true);
            } catch (error) {
                const fetcherError = error as FetcherError;
                if (fetcherError.status === 400) {
                    customErrorToast(imageModerationError);
                }
            }
        }
    };

    const handleRemove = () => {
        setShowConfirmDialog(true);
    };

    const handleConfirmRemove = (onChange: (value: string) => void) => {
        onChange('');
        setImageUploaded(false);
        setImage(null);
        setFile(null);
        setErrorMessage(null);
        setImageDimensions(null);
        setShowConfirmDialog(false);
    };

    const handleReset = () => {
        setErrorMessage(null);
        setFile(null);
        setImage(null);
        setImageDimensions(null);
        setImageUploaded(false);
    };

    const basePath = process.env.EXPO_PUBLIC_IMAGE_SERVE_HOST ?? '';

    return (
        <Controller
            control={control}
            name={formValue}
            render={({ field: { onChange, value } }) => (
                <Stack spacing={1}>
                    {label ? (
                        <FormLabel required={required} sx={{ color: 'black', fontWeight: 'medium', mb: 0.5 }}>
                            {label}
                        </FormLabel>
                    ) : null}
                    <Stack
                        onClick={() => document.getElementById(`fileInput-${formValue}`)?.click()}
                        sx={{
                            '&:hover': { borderColor: '#999' },
                            backgroundImage: image
                                ? `url(${image})`
                                : value
                                  ? `url(${basePath}/${value as string}.webp)`
                                  : '',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: 'cover',
                            border: '4px dashed',
                            borderRadius: 2,
                            cursor: 'pointer',
                        }}
                    >
                        <Stack
                            spacing={2}
                            sx={{
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 3,
                                px: 8,
                                textAlign: 'center',
                            }}
                        >
                            <Stack
                                sx={{
                                    alignItems: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                    color: 'white',
                                    justifyContent: 'center',
                                    p: 2,
                                    textAlign: 'center',
                                    width: 200,
                                }}
                            >
                                <ImageIcon sx={{ height: 60, width: 60 }} />
                                <Typography variant="body2">{helperText}</Typography>
                                {imageDimensions ? (
                                    <Typography sx={{ mt: 1 }} variant="caption">
                                        {imageDimensions.width}x{imageDimensions.height} (AR:{' '}
                                        {imageDimensions.aspectRatio})
                                    </Typography>
                                ) : null}
                                <input
                                    accept="image/*"
                                    id={`fileInput-${formValue}`}
                                    onChange={handleImageChange}
                                    style={{ display: 'none' }}
                                    type="file"
                                />
                            </Stack>
                        </Stack>
                    </Stack>

                    {errorMessage ? (
                        <Stack sx={{ alignItems: 'flex-end' }}>
                            <Typography color="error" sx={{ mt: 1 }} variant="caption">
                                {errorMessage}
                            </Typography>
                            <Button onClick={handleReset} size="small" sx={{ mt: 1 }} variant="contained">
                                Clear
                            </Button>
                        </Stack>
                    ) : (
                        <Stack
                            direction="row"
                            sx={{
                                justifyContent: 'space-between',
                                pt: 1,
                            }}
                        >
                            {value ? (
                                <Button color="error" onClick={handleRemove} variant="outlined">
                                    Remove
                                </Button>
                            ) : (
                                <Box />
                            )}
                            {file && !imageUploaded ? (
                                <Button onClick={() => handleImageUpload(onChange)} variant="contained">
                                    {buttonText}
                                </Button>
                            ) : (
                                <Box />
                            )}
                        </Stack>
                    )}

                    {/* Confirmation Dialog */}
                    <Dialog
                        aria-describedby="confirm-dialog-description"
                        aria-labelledby="confirm-dialog-title"
                        onClose={() => setShowConfirmDialog(false)}
                        open={showConfirmDialog}
                    >
                        <DialogTitle id="confirm-dialog-title">Remove Image</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="confirm-dialog-description">
                                Are you sure you want to remove this image? This action will clear the image from both
                                the input and update the form.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
                            <Button color="error" onClick={() => handleConfirmRemove(onChange)} variant="contained">
                                Remove
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Stack>
            )}
            rules={{
                required: required ? `${label ?? formValue} cannot be empty` : false,
            }}
        />
    );
}
